import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { Logo } from "@/components/Logo";

export default function Apply() {
  const { code } = useParams();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API_BASE}/referral/${code}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "This link is invalid or expired."));
  }, [code]);

  const goRegister = () => nav(`/register?ref=${encodeURIComponent(code)}`);

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-200">
        <div className="mx-auto max-w-6xl px-6 h-16 flex items-center justify-between">
          <Logo />
          <Link
            to="/login"
            className="small-caps text-slate-600 hover:text-[#1F2937]"
            data-testid="apply-signin-link"
          >
            Already have an account? Sign in
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-24" data-testid="apply-landing">
        {error ? (
          <div>
            <div className="small-caps text-slate-500">Link unavailable</div>
            <h1 className="mt-6 font-semibold text-[64px] leading-[0.95] tracking-tight text-[#1F2937]">
              This link isn&apos;t <span className="italic">active.</span>
            </h1>
            <p className="mt-6 max-w-lg text-[16px] leading-[1.7] text-slate-600" data-testid="apply-error">
              {error}
            </p>
            <Link
              to="/"
              className="mt-10 inline-flex items-center gap-4 group"
              data-testid="apply-home-link"
            >
              <span className="h-[1px] w-16 bg-slate-800 transition-all group-hover:w-24" />
              <span className="small-caps text-[#1F2937]">Return home</span>
            </Link>
          </div>
        ) : !info ? (
          <div className="small-caps text-slate-400">Loading…</div>
        ) : (
          <div>
            <div className="small-caps text-slate-500" data-testid="apply-referrer-role">
              Referred by your {info.role === "broker" ? "advisor" : "loan officer"}
            </div>
            <h1 className="mt-6 font-semibold text-[64px] leading-[0.95] tracking-tight text-[#1F2937]">
              Apply with{" "}
              <span className="italic" data-testid="apply-referrer-name">
                {info.company || info.name}.
              </span>
            </h1>
            <div className="mt-8 hair-y py-6 grid grid-cols-2 gap-8 max-w-xl">
              <div>
                <div className="small-caps text-slate-500">Point of Contact</div>
                <div className="mt-2 font-semibold text-[18px] text-[#1F2937]">{info.name}</div>
                {info.title && (
                  <div className="small-caps text-slate-400 mt-1">{info.title}</div>
                )}
              </div>
              <div>
                <div className="small-caps text-slate-500">Referral Code</div>
                <div className="mt-2 font-mono text-[16px] text-[#1F2937]" data-testid="apply-code">
                  {info.referral_code}
                </div>
              </div>
            </div>
            <p className="mt-10 max-w-lg text-[16.5px] leading-[1.7] text-slate-600">
              Create your Venture Nationals account and your application will be
              routed directly to {info.name.split(" ")[0]}. It takes about 60 seconds.
            </p>
            <div className="mt-10 flex items-center gap-8 flex-wrap">
              <button
                onClick={goRegister}
                className="group inline-flex items-center gap-4"
                data-testid="apply-continue-btn"
              >
                <span className="h-[1px] w-16 bg-slate-800 transition-all group-hover:w-24" />
                <span className="small-caps text-[#1F2937]">Begin Application →</span>
              </button>
              <Link
                to={`/login?ref=${encodeURIComponent(code)}`}
                className="small-caps text-slate-500 hover:text-[#1F2937]"
                data-testid="apply-signin-fallback"
              >
                I already have an account
              </Link>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
